import { type RefObject, useLayoutEffect, useRef } from "react";

/**
 * Двусторонний sticky для боковой колонки многоколоночной раскладки.
 *
 * Обычный `position: sticky` умеет прилипать только к ОДНОМУ краю. Колонка
 * выше экрана от этого становится частично недоступной: прибитая к верху, она
 * никогда не показывает свой низ, сколько страницу ни крути. Здесь колонка
 * ведёт себя так, как в подобных раскладках ожидают:
 *
 * - вниз — едет вместе с содержимым, пока её нижний край не дойдёт до низа
 *   экрана, и дальше стоит: конец колонки виден всё время прокрутки;
 * - вверх — снова едет с содержимым, пока верхний край не дойдёт до верха,
 *   и дальше стоит: начало колонки видно всё время обратной прокрутки.
 *
 * Колонка, целиком помещающаяся в экран, вырождается в обычный верхний
 * sticky — отдельной ветки для этого случая не нужно, её даёт кламп.
 *
 * Механика: элемент остаётся `position: sticky`, а хук на каждом событии
 * прокрутки двигает его `top` ровно на пройденный путь. Пока `top` не упёрся
 * в границу, элемент выглядит примёрзшим к документу; упёрся — прилип к краю
 * экрана. Значение пишется прямо в стиль элемента, без состояния React: это
 * событие прокрутки, а не изменение данных, и перерисовывать на нём дерево
 * нечем и незачем.
 *
 * Применяется и к левому меню, и к правым колонкам разделов — правило одно,
 * поэтому и код один.
 */

/** Оболочка витрины переключается на две колонки на `lg` (Tailwind, 64rem). */
const DESKTOP_QUERY = "(min-width: 64rem)";

function readPx(name: string, fallback: number): number {
	const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
	const value = Number.parseFloat(raw);
	return Number.isFinite(value) ? value : fallback;
}

/**
 * Верхний упор — липкая шапка целиком (поле канвы + карточка шапки) плюс тот
 * же зазор, что отделяет шапку от содержимого. Читается из токенов, а не
 * набирается числом: высота шапки живёт в `tokens.css`, и вторая её копия
 * разойдётся с первой ровно тогда, когда шапку поменяют.
 */
function topLimit(): number {
	return readPx("--layout-shell-pad", 10) * 2 + readPx("--layout-header-h", 64);
}

/**
 * Новое значение `top` после прокрутки на `dy` пикселей.
 *
 * Вся суть здесь: смещение меняется ровно на пройденный путь, поэтому колонка
 * выглядит примёрзшей к документу, — до упора. Вниз упор нижний (`minTop`),
 * вверх верхний (`maxTop`). Для колонки, помещающейся в экран, `minTop`
 * равен `maxTop`, и оба выражения дают одно и то же значение.
 */
export function nextStickyTop(args: {
	top: number;
	dy: number;
	minTop: number;
	maxTop: number;
}): number {
	const { top, dy, minTop, maxTop } = args;
	return dy > 0 ? Math.max(minTop, top - dy) : Math.min(maxTop, top - dy);
}

export function useStickyColumn<T extends HTMLElement>(): RefObject<T | null> {
	const ref = useRef<T>(null);

	// useLayoutEffect, а не useEffect: `top` задаётся только здесь, и с обычным
	// эффектом первый кадр колонка рисовалась бы без него.
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;

		const media = window.matchMedia(DESKTOP_QUERY);
		let maxTop = 0;
		let minTop = 0;
		let top = 0;
		let prevY = window.scrollY;
		let attached = false;

		const measure = () => {
			maxTop = topLimit();
			// Нижний упор: нижний край колонки у низа экрана с тем же полем
			// канвы. Для колонки ниже экрана значение больше верхнего упора,
			// поэтому кламп ниже схлопывает диапазон в одну точку — обычный
			// верхний sticky.
			const gap = readPx("--layout-shell-pad", 10);
			minTop = Math.min(maxTop, window.innerHeight - gap - el.offsetHeight);
			top = Math.min(maxTop, Math.max(minTop, top));
			el.style.top = `${top}px`;
		};

		const onScroll = () => {
			const y = window.scrollY;
			const dy = y - prevY;
			prevY = y;
			if (dy === 0) return;
			top = nextStickyTop({ top, dy, minTop, maxTop });
			el.style.top = `${top}px`;
		};

		// Высота колонки меняется без перезагрузки страницы: раскрылся
		// аккордеон, доехал запрос, сменился язык. Без наблюдателя нижний упор
		// остался бы посчитанным по старой высоте, и низ колонки снова уехал
		// бы за экран.
		const observer = new ResizeObserver(measure);

		const attach = () => {
			if (attached) return;
			attached = true;
			prevY = window.scrollY;
			top = topLimit();
			measure();
			observer.observe(el);
			window.addEventListener("scroll", onScroll, { passive: true });
			window.addEventListener("resize", measure);
		};

		const detach = () => {
			if (!attached) return;
			attached = false;
			observer.disconnect();
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", measure);
			// На мобильном колонка не липкая и лежит в общем потоке: инлайновый
			// `top` там ничего не значит, но и висеть ему незачем.
			el.style.removeProperty("top");
		};

		const sync = () => (media.matches ? attach() : detach());
		sync();
		media.addEventListener("change", sync);

		return () => {
			media.removeEventListener("change", sync);
			detach();
		};
	}, []);

	return ref;
}
